/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { UserStatus } from '@gh/builder-shared-data';
import {
  CELL_FOOTER_TITLE_CLASS,
  CELL_TEXT_RIGHT_CLASS,
  createDefaultGridModel,
  HEADER_CELL_TEXT_RIGHT_CLASS,
  identityValueFormatter,
  ROW_DIRTY_CLASS,
  ROW_FOOTER_SUMMARY_CLASS,
  ROW_FOOTER_TITLE_CLASS,
} from '@gh/common-internal-shared';
import { NumberTypes, queryFormEditable, State, whenDirty } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildEditableNumberColumn,
  buildNumberColumn,
  EditableCellState,
  GridModel,
  ManualGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { MONTH_LOGICAL_NAMES } from '@gh/core-util';
import { ColDef, GridOptions } from 'ag-grid-community';
import {
  getSbListItemId,
  SbListItem,
  sbListItemFieldState,
  SbListItemType,
  validateSbListItemField,
} from '../../resources/sb-list.model';
import { SbListCommandService } from './sb-list-command.service';
import { SbListState } from './sb-list.state';

@Injectable()
export class SbGridDefinition extends ManualGridDefinitionWithModel {
  constructor(private translationService: TranslationService,
              private state: State<SbListState>,
              private commands: SbListCommandService) {
    super();
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel({
      editable: this.state.project(queryFormEditable),
      getId: getSbListItemId,
      getCellDef: sbListItemFieldState,
      validate: validateSbListItemField,
      command: (entity, name, value) => this.commands.setItemField(getSbListItemId(entity), name, value),
    });
  }

  buildGridOptions(): GridOptions {
    return {
      rowSelection: 'single',
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: SbListItem }) => whenDirty(data),
        [ROW_FOOTER_TITLE_CLASS]: ({ data }: { data: SbListItem }) => data.type === SbListItemType.FooterTitle,
        [ROW_FOOTER_SUMMARY_CLASS]: ({ data }: { data: SbListItem }) => data.type === SbListItemType.FooterSummary,
      },
    };
  }

  buildColumnDefs(): ColDef[] {
    return [{
      field: 'salespersonName',
      headerName: this.translationService.get('lbl_name'),
      width: 100,
      sort: 'asc',
    }, {
      field: 'status',
      headerName: this.translationService.get('lbl_status'),
      width: 200,
      cellClass: ({ data }: { data: SbListItem }) => ({
        [UserStatus.Active]: 'cell--positive',
        [UserStatus.Inactive]: 'cell--unimportant',
        [UserStatus.Transferred]: 'cell--important',
        [UserStatus.Deleted]: 'cell--negative',
      }[data.status]),
    }, ...MONTH_LOGICAL_NAMES.map((month) => ({
      field: month,
      headerName: this.translationService.get(`lbl_${month}_middle`),
      headerClass: HEADER_CELL_TEXT_RIGHT_CLASS,
      cellClassRules: {
        [CELL_TEXT_RIGHT_CLASS]: ({ data }: { data: SbListItem }) => data.type !== SbListItemType.Item,
        [CELL_FOOTER_TITLE_CLASS]: ({ data }: { data: SbListItem }) => data.type === SbListItemType.FooterTitle,
      },
      width: 50,
      sortable: false,
      ...buildEditableNumberColumn(NumberTypes.Count, this.model.getCellAccessor(month)),
      pinnedRowValueFormatter: identityValueFormatter,
      pinnedRowCellRenderer: ({ value, data }: { value: EditableCellState; data: SbListItem }) => {
        switch (data.type) {
          case SbListItemType.FooterTitle:
            return value.value;
          default:
            return NumberTypes.Count.format(value.value);
        }
      },
    })), {
      field: 'total',
      headerName: this.translationService.get('lbl_total'),
      headerClass: HEADER_CELL_TEXT_RIGHT_CLASS,
      cellClass: CELL_TEXT_RIGHT_CLASS,
      width: 200,
      sortable: false,
      ...buildNumberColumn(NumberTypes.Count),
      pinnedRowValueFormatter: identityValueFormatter,
      pinnedRowCellRenderer: ({ value, data }: { value: any; data: SbListItem }) => {
        switch (data.type) {
          case SbListItemType.FooterTitle:
            return value;
          default:
            // tslint:disable-next-line:max-line-length
            return `<span class="text-uppercase text-bold">${this.translationService.get('lbl_total')}:</span> <span class="text-main text-bold">${NumberTypes.Count.format(
              value)}</span>`;
        }
      },
    }];
  }
}
